import React, { useState } from 'react';
import { X } from 'lucide-react';
import api from '../../services/api';
import SproutSyncConfig from './SproutSyncConfig';
import CustomDomainStatus from './CustomDomainStatus';

const CampaignSettings = ({ campaignId, formData, savedCustomDomain, onChange, onSubmit, saving, error }) => {
  const [activeTab, setActiveTab] = useState('general');
  const [termsText, setTermsText] = useState('');
  const [termsFile, setTermsFile] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateMessage, setGenerateMessage] = useState({ type: '', text: '' });

  const handleGeneratePrompts = async () => {
    if (!termsFile && !termsText.trim()) {
      setGenerateMessage({ type: 'error', text: 'Paste the terms and conditions or upload a PDF first.' });
      return;
    }

    setGenerating(true);
    setGenerateMessage({ type: '', text: '' });
    try {
      const body = new FormData();
      if (termsFile) {
        body.append('file', termsFile);
      } else {
        body.append('text', termsText);
      }

      const response = await api.post('/admin/campaigns/receipt-prompts/generate', body, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      Object.entries(response.data).forEach(([name, value]) => {
        onChange({ target: { name, value, type: 'textarea' } });
      });
      setGenerateMessage({ type: 'success', text: 'Prompts filled in. Review them, then click Save Settings.' });
      setShowTermsModal(false);
      setTermsText('');
      setTermsFile(null);
    } catch (err) {
      setGenerateMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to generate prompts.'
      });
    } finally {
      setGenerating(false);
    }
  };

  const tabs = [
    { id: 'general', label: 'Settings' },
    { id: 'receipt', label: 'Receipt Validation' },
    { id: 'tracking', label: 'Tracking' },
    ...(campaignId ? [{ id: 'integration', label: 'me&u Integration' }] : [])
  ];

  const tabClass = (id) =>
    `py-2 px-1 border-b-2 font-medium text-sm ${
      activeTab === id
        ? 'border-teal-500 text-teal-600'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`;

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200">
        <nav role="tablist" className="-mb-px flex space-x-8">
        {tabs.map(tab => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={tabClass(tab.id)}
          >
            {tab.label}
          </button>
        ))}
        </nav>
      </div>

    <form onSubmit={onSubmit} className={`space-y-6 ${activeTab === 'integration' ? 'hidden' : ''}`}>
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">
          {error}
        </div>
      )}

      <div className={activeTab === 'general' ? '' : 'hidden'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Custom Domain (optional)
            </label>
            <input
              type="text"
              name="custom_domain"
              value={formData.custom_domain}
              onChange={onChange}
              placeholder="e.g., winbali.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-2">
              Use only the hostname (no https://). If set, public pages will resolve under this domain.
            </p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              URL Slug
            </label>
            <input
              type="text"
              name="slug"
              value={formData.slug || ''}
              onChange={onChange}
              placeholder="e.g., win-a-trip-to-bali"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-2">
              Lowercase letters, numbers and hyphens only. Changing this breaks existing links and QR codes.
            </p>
          </div>
        </div>
        <div className="mt-3 bg-blue-50 border border-blue-200 text-blue-800 text-xs rounded-lg p-3 space-y-3">
          <p>
            Point the vanity domain (e.g. <code>wingagebali.com.au</code>) at the same host that already serves your campaign (for example <code>gooddrinkspromotion.dailypress.com.au</code>). Add the records below at your DNS provider, save these settings, then use “Enable domain & HTTPS” to finish setup.
          </p>
          <div className="overflow-hidden rounded-lg border border-blue-200 bg-white text-gray-700">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-blue-100 text-blue-900 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2 font-semibold">Type</th>
                  <th className="px-4 py-2 font-semibold">Name</th>
                  <th className="px-4 py-2 font-semibold">Value</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-blue-100">
                  <td className="px-4 py-2 text-blue-900 font-medium">A</td>
                  <td className="px-4 py-2">@ (root)</td>
                  <td className="px-4 py-2 font-mono">34.40.151.217</td>
                </tr>
                <tr className="border-t border-blue-100">
                  <td className="px-4 py-2 text-blue-900 font-medium">CNAME</td>
                  <td className="px-4 py-2">www (optional)</td>
                  <td className="px-4 py-2 font-mono">gooddrinkspromotion.dailypress.com.au</td>
                </tr>
              </tbody>
            </table>
          </div>
          <CustomDomainStatus
            campaignId={campaignId}
            savedDomain={savedCustomDomain}
            hasUnsavedChange={(formData.custom_domain || '').trim().toLowerCase() !== (savedCustomDomain || '').trim().toLowerCase()}
          />
        </div>
      </div>

      <div className={activeTab === 'tracking' ? '' : 'hidden'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Meta Pixel ID (optional)
            </label>
            <input
              type="text"
              name="meta_pixel_id"
              value={formData.meta_pixel_id || ''}
              onChange={onChange}
              inputMode="numeric"
              placeholder="e.g., 145314645274657"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-2">
              When set, the Meta Pixel loads on this campaign's public page (PageView) and fires Complete Registration after each successful entry.
            </p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              TikTok Pixel ID (optional)
            </label>
            <input
              type="text"
              name="tiktok_pixel_id"
              value={formData.tiktok_pixel_id || ''}
              onChange={onChange}
              placeholder="e.g., DBG2OCJC77UFSQ3BSHLG"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-2">
              When set, the TikTok Pixel loads on this campaign's public page (page view) and fires Complete Registration after each successful entry.
            </p>
          </div>
        </div>
      </div>

      <div className={activeTab === 'receipt' ? '' : 'hidden'}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Receipt Validation Prompts
            </label>
            <p className="text-xs text-gray-500">
              Customize how receipts are extracted and validated. Leaving fields blank reverts to platform defaults.
            </p>
            {generateMessage.text && !showTermsModal && (
              <p className={`text-xs mt-2 ${generateMessage.type === 'error' ? 'text-red-600' : 'text-teal-700'}`}>
                {generateMessage.text}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => { setGenerateMessage({ type: '', text: '' }); setShowTermsModal(true); }}
            className="shrink-0 px-4 py-2 bg-teal-700 text-white rounded-xl hover:bg-teal-600 text-sm"
          >
            Auto-fill from Terms &amp; Conditions
          </button>
        </div>

        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Step 1: Receipt OCR Extraction</h4>
          <p className="text-xs text-gray-500 mb-3">
            Customize how receipt data is extracted. Use <code>{'{{OCR_TEXT}}'}</code> in the user prompt to inject the scanned receipt text. The OCR text will be truncated to the first 2,500 characters before substitution.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                System Prompt
              </label>
              <textarea
                name="step1_system_prompt"
                value={formData.step1_system_prompt || ''}
                onChange={onChange}
                rows={10}
                placeholder="Extract receipt data..."
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                User Prompt
              </label>
              <textarea
                name="step1_user_prompt"
                value={formData.step1_user_prompt || ''}
                onChange={onChange}
                rows={10}
                placeholder={'Receipt:\n{{OCR_TEXT}}'}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono"
              />
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Step 2: Matching Receipt & Selected Product</h4>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                System Prompt
              </label>
              <textarea
                name="step2_system_prompt"
                value={formData.step2_system_prompt || ''}
                onChange={onChange}
                rows={8}
                placeholder="You are a product matching validator..."
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                User Prompt
              </label>
              <textarea
                name="step2_user_prompt"
                value={formData.step2_user_prompt || ''}
                onChange={onChange}
                rows={8}
                placeholder={'Receipt Products:\n{{RECEIPT_PRODUCTS}}\n\nSelected Product: "{{SELECTED_PRODUCT}}"'}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono"
              />
              <p className="text-xs text-gray-500 mt-2">
                Use <code>{'{{RECEIPT_PRODUCTS}}'}</code> and <code>{'{{SELECTED_PRODUCT}}'}</code> placeholders.
              </p>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Step 3: Validating in Campaign</h4>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                System Prompt
              </label>
              <textarea
                name="step3_system_prompt"
                value={formData.step3_system_prompt || ''}
                onChange={onChange}
                rows={8}
                placeholder="You are a product database validator..."
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                User Prompt
              </label>
              <textarea
                name="step3_user_prompt"
                value={formData.step3_user_prompt || ''}
                onChange={onChange}
                rows={8}
                placeholder={'Receipt Products:\n{{RECEIPT_PRODUCTS}}\n\nAvailable Campaign Products:\n{{AVAILABLE_PRODUCTS}}'}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono"
              />
              <p className="text-xs text-gray-500 mt-2">
                Use <code>{'{{RECEIPT_PRODUCTS}}'}</code> and <code>{'{{AVAILABLE_PRODUCTS}}'}</code> placeholders.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="pt-4 border-t">
        <button
          type="submit"
          disabled={saving}
          className="px-6 py-3 bg-teal-700 text-white rounded-xl hover:bg-teal-600 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save Settings'}
        </button>
      </div>
    </form>

      {campaignId && activeTab === 'integration' && <SproutSyncConfig campaignId={campaignId} />}

      {showTermsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Auto-fill from Terms &amp; Conditions</h3>
              <button
                type="button"
                onClick={() => !generating && setShowTermsModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-4">
              <p className="text-sm text-gray-600">
                Paste the promotion's terms and conditions or upload them as a PDF. This fills all Step 1-3 prompts and replaces what is currently there. Nothing is saved until you click Save Settings.
              </p>
              <textarea
                value={termsText}
                onChange={(e) => setTermsText(e.target.value)}
                rows={10}
                disabled={!!termsFile || generating}
                placeholder="Paste terms and conditions here..."
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100"
              />
              <div className="flex flex-wrap items-center gap-3">
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  disabled={generating}
                  onChange={(e) => setTermsFile(e.target.files?.[0] || null)}
                  className="text-sm text-gray-700"
                />
                {termsFile && (
                  <button type="button" onClick={() => setTermsFile(null)} className="text-xs text-gray-600 underline">
                    Remove PDF
                  </button>
                )}
              </div>
              {generateMessage.type === 'error' && (
                <p className="text-sm text-red-600">{generateMessage.text}</p>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowTermsModal(false)}
                disabled={generating}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleGeneratePrompts}
                disabled={generating}
                className="px-4 py-2 bg-teal-700 text-white rounded-xl hover:bg-teal-600 disabled:opacity-50"
              >
                {generating ? 'Generating…' : 'Auto-fill Prompts'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignSettings;
